import { useEffect, useRef, type ReactNode } from 'react';
import { Bot, X } from 'lucide-react';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
  /** Optional rich content rendered under an assistant bubble (e.g. product cards). */
  extra?: ReactNode;
}

export interface ChatShellConversationProps {
  messages: ChatMessage[];
  loading?: boolean;
  /** A notice pinned above the messages (e.g. "AI offline — data lookups still work"). */
  banner?: ReactNode;
  /** Shown centered when there are no messages yet. */
  emptyHint?: ReactNode;
  /** The input area — differs per bot (free text vs crop picker). */
  composer: ReactNode;
}

interface ChatShellProps extends Partial<ChatShellConversationProps> {
  title: string;
  subtitle?: string;
  open: boolean;
  onToggle: (open: boolean) => void;
  fabAriaLabel?: string;
  /** FAB glyph (default: the bot). */
  fabIcon?: ReactNode;
  /** Pinned to the FAB's top-right corner — e.g. an unread count pill. */
  fabBadge?: ReactNode;
  /** Glyph left of the title in the header (default: the bot). */
  headerIcon?: ReactNode;
  /** Extra header buttons, rendered just before Close. */
  headerActions?: ReactNode;
  /** A strip under the header — e.g. the launcher's tab bar. */
  tabs?: ReactNode;
  /**
   * A custom body. When set it replaces the built-in banner / message list / composer, which is
   * what the combined launcher does for its tabs (it renders `ChatShellConversation` itself for AI).
   */
  children?: ReactNode;
  /**
   * `tall` — ~600 px, room for a team-chat thread, and full-screen on a phone. The default keeps the
   * original 540 px AI widget exactly as it was (public crop advisor, farmer / retailer sessions).
   */
  size?: 'default' | 'tall';
}

/**
 * The bot conversation body: optional banner, the bubble list (auto-scrolls to the newest) and the
 * composer. Rendered by `ChatShell` itself, and by the combined launcher inside its AI tab.
 */
export function ChatShellConversation({ messages, loading, banner, emptyHint, composer }: ChatShellConversationProps) {
  const bodyRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: 'smooth' });
  }, [messages, loading]);

  return (
    <>
      {banner}

      {/* Messages */}
      <div ref={bodyRef} className="min-h-0 flex-1 space-y-2 overflow-y-auto bg-surface-2 p-3 dark:bg-black/20">
        {messages.length === 0 && emptyHint && (
          <div className="mt-8 px-4 text-center text-sm text-subtle">{emptyHint}</div>
        )}
        {messages.map((m, i) => (
          <div key={i} className={m.role === 'user' ? 'flex justify-end' : 'flex flex-col items-start'}>
            <div
              className={
                m.role === 'user'
                  ? 'max-w-[82%] whitespace-pre-wrap rounded-2xl rounded-br-sm bg-brand-600 px-3 py-2 text-sm leading-relaxed text-white'
                  : 'max-w-[86%] whitespace-pre-wrap rounded-2xl rounded-bl-sm border border-line bg-surface px-3 py-2 text-sm leading-relaxed text-content'
              }
            >
              {m.content}
            </div>
            {m.role === 'assistant' && m.extra}
          </div>
        ))}
        {loading && (
          <div className="flex items-center gap-1.5 px-1 py-1 text-subtle">
            <span className="size-1.5 animate-bounce rounded-full bg-current [animation-delay:-0.2s]" />
            <span className="size-1.5 animate-bounce rounded-full bg-current [animation-delay:-0.1s]" />
            <span className="size-1.5 animate-bounce rounded-full bg-current" />
          </div>
        )}
      </div>

      {/* Composer */}
      <div className="border-t border-line bg-surface p-2 dark:bg-[#1a201a]">{composer}</div>
    </>
  );
}

const PANEL_BASE =
  'gb-assistant-floating fixed bottom-6 right-6 z-[1000] flex w-[380px] max-w-[calc(100vw-32px)] flex-col overflow-hidden rounded-2xl border border-line bg-surface shadow-[0_16px_48px_rgba(0,0,0,0.32)] dark:bg-[#1a201a]';
const PANEL_SIZE = {
  default: 'h-[540px] max-h-[calc(100vh-96px)]',
  // Phone: the whole screen (a thread needs it). sm+: the same floating card, just taller.
  tall: 'h-[600px] max-h-[calc(100dvh-96px)] max-sm:inset-0 max-sm:h-dvh max-sm:max-h-none max-sm:w-full max-sm:max-w-none max-sm:rounded-none max-sm:border-0',
} as const;

/**
 * Shared floating chat modal (FAB → panel) used by BOTH the internal assistant and the public crop
 * advisor, so they look identical ("same modal"). Self-contained Tailwind (no app theme tokens) so it
 * renders consistently in the authed shell (light/dark) and on the public marketing site (light).
 */
export function ChatShell({
  title, subtitle, open, onToggle, messages, loading, banner, emptyHint, composer, fabAriaLabel,
  fabIcon, fabBadge, headerIcon, headerActions, tabs, children, size = 'default',
}: ChatShellProps) {
  // Close on Escape while open — unless something on top (a dialog, drawer, tooltip, lightbox)
  // already took that Escape: Radix layers preventDefault the key before dismissing themselves, so
  // one press closes only the top-most thing, not the dialog AND the panel behind it.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (document.body.hasAttribute('data-scroll-locked')) return;
      onToggle(false);
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onToggle]);

  if (!open) {
    return (
      <button
        type="button"
        aria-label={fabAriaLabel ?? 'Open assistant'}
        onClick={() => onToggle(true)}
        className="gb-assistant-floating fixed bottom-6 right-6 z-[1000] grid size-14 place-items-center rounded-full bg-brand-600 text-white shadow-[0_6px_18px_rgba(0,0,0,0.28)] transition hover:bg-brand-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-400"
      >
        {fabIcon ?? <Bot size={24} />}
        {fabBadge && <span className="pointer-events-none absolute -right-0.5 -top-0.5">{fabBadge}</span>}
      </button>
    );
  }

  return (
    <div role="dialog" aria-label={title} className={`${PANEL_BASE} ${PANEL_SIZE[size]}`}>
      {/* Header */}
      <div className="flex items-center gap-2 border-b border-line bg-brand-600 px-4 py-3 text-white">
        {headerIcon ?? <Bot size={18} className="shrink-0" />}
        <div className="min-w-0 flex-1">
          <div className="truncate type-body-strong leading-tight">{title}</div>
          {subtitle && <div className="truncate text-2xs leading-tight text-white/80">{subtitle}</div>}
        </div>
        {headerActions}
        <button type="button" aria-label="Close" onClick={() => onToggle(false)} className="grid size-7 place-items-center rounded-md text-white/90 transition hover:bg-white/15">
          <X size={16} />
        </button>
      </div>

      {tabs}

      {children ?? (
        <ChatShellConversation
          messages={messages ?? []}
          loading={loading}
          banner={banner}
          emptyHint={emptyHint}
          composer={composer}
        />
      )}
    </div>
  );
}
